import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Ban,
  CheckCircle2,
  ExternalLink,
  KeyRound,
  LogOut,
  Plus,
  RotateCcw,
  Users,
} from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';

import { kinds, type EnvKey } from '@/app/site/scope';
import { CatalogSearch } from '@/features/catalog/Catalog';
import catalog from '@/features/catalog/Catalog.module.scss';
import type { FieldBody, SiteUserBody } from '@/shared/api/generated/model';
import {
  siteUsersBlock,
  siteUsersCreate,
  siteUsersList,
  siteUsersLogout,
  siteUsersReset,
  siteUsersVerify,
} from '@/shared/api/generated/site-users/site-users';
import { recordsMarkRead } from '@/shared/api/generated/records/records';
import { ApiError } from '@/shared/api/http';
import { describeWhen } from '@/shared/lib/dates';
import { pluralize } from '@/shared/lib/plural';
import { useDebounced } from '@/shared/lib/useDebounced';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { toast } from '@/shared/ui/Toast';

import { submissionFields, valueText } from '../FormsPage/submissions';
import styles from './UsersPage.module.scss';
import { PROVIDER_NAME, VISITORS } from './words';

const PER_PAGE = 50;

export interface VisitorsListProps {
  siteId: string;
  env: EnvKey;
  /** Коллекция пользователей и сколько в ней новых регистраций. */
  poolId: string;
  unread: number;
  fields: FieldBody[];
  canWrite: boolean;
}

/** Посетители: поиск по почте и имени, окно посетителя с действиями. */
export function VisitorsList({ siteId, env, poolId, unread, fields, canWrite }: VisitorsListProps) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const needle = useDebounced(search.trim(), 300);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<SiteUserBody>();
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: ['site-users', siteId, env, needle, page],
    queryFn: async () => {
      const response = await siteUsersList(siteId, kinds[env], {
        page,
        per_page: PER_PAGE,
        ...(needle === '' ? {} : { search: needle }),
      });

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на список посетителей');
      }

      return response.data;
    },
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['site-users', siteId] });
  };

  // Новые регистрации — как уведомления: открыл список — увидел, счётчик в
  // сайдбаре гаснет.
  useEffect(() => {
    if (unread === 0) {
      return;
    }

    void recordsMarkRead(poolId, { read: true }).then(() => {
      void queryClient.invalidateQueries({ queryKey: ['content-types', siteId] });
    });
  }, [poolId, unread, siteId, queryClient]);

  // Действие над посетителем: ответ — он же обновлённый.
  const act = (run: Promise<{ data: unknown }>, done: string) => {
    run
      .then((response) => {
        toast.success(done);
        setOpen(response.data as SiteUserBody);
        refresh();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof ApiError ? cause.detail : 'Не получилось');
      });
  };

  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PER_PAGE));
  const shown = submissionFields(fields);

  return (
    <div className={styles['tab']}>
      <div className={catalog['bar']}>
        <CatalogSearch
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="Поиск по почте или имени"
        />
        {canWrite && (
          <Button
            className={styles['bar__end']}
            iconLeft={<Plus size={15} />}
            onClick={() => {
              setCreating(true);
            }}
          >
            Завести посетителя
          </Button>
        )}
      </div>

      {list.isPending ? null : items.length === 0 ? (
        <EmptyState
          compact
          icon={<Users />}
          title={needle === '' ? 'Посетителей пока нет' : 'Никого не нашли'}
          description={
            needle === ''
              ? 'Они появятся, когда зарегистрируются на сайте. Как подключить вход — на вкладке «API».'
              : 'Поиск — по почте и имени.'
          }
        />
      ) : (
        <>
          <div className={catalog['table']}>
            <table>
              <thead>
                <tr>
                  <th>Почта</th>
                  <th>Имя</th>
                  <th>Состояние</th>
                  <th>Входил</th>
                  <th>Зарегистрирован</th>
                </tr>
              </thead>
              <tbody>
                {items.map((user) => (
                  <tr
                    key={user.id}
                    className={styles['item']}
                    tabIndex={0}
                    onClick={() => {
                      setOpen(user);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        setOpen(user);
                      }
                    }}
                  >
                    <td>{user.email}</td>
                    <td>{user.title === user.email ? '—' : user.title}</td>
                    <td>
                      <State user={user} />
                    </td>
                    <td className={styles['when']}>
                      {user.last_login_at === undefined ? '—' : describeWhen(user.last_login_at)}
                    </td>
                    <td className={styles['when']}>{describeWhen(user.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className={styles['pager']}>
              <Button
                size="sm"
                variant="outline"
                tone="neutral"
                disabled={page <= 1}
                onClick={() => {
                  setPage(page - 1);
                }}
              >
                Новее
              </Button>
              <span>
                {page} из {pages} · {pluralize(total, VISITORS)}
              </span>
              <Button
                size="sm"
                variant="outline"
                tone="neutral"
                disabled={page >= pages}
                onClick={() => {
                  setPage(page + 1);
                }}
              >
                Раньше
              </Button>
            </div>
          )}
        </>
      )}

      <Dialog
        open={open !== undefined}
        onOpenChange={(next) => {
          if (!next) {
            setOpen(undefined);
          }
        }}
      >
        {open !== undefined && (
          <DialogContent
            title={open.email}
            description={`Зарегистрирован ${describeWhen(open.created_at)}${
              open.last_login_at === undefined
                ? ''
                : ` · входил ${describeWhen(open.last_login_at)}`
            }`}
            size="md"
            footer={
              canWrite ? (
                <div className={styles['dialog__actions']}>
                  {!open.email_verified && (
                    <Button
                      variant="ghost"
                      tone="neutral"
                      iconLeft={<CheckCircle2 size={15} />}
                      onClick={() => {
                        act(siteUsersVerify(open.id), 'Почта подтверждена');
                      }}
                    >
                      Подтвердить почту
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    tone="neutral"
                    iconLeft={<KeyRound size={15} />}
                    onClick={() => {
                      act(siteUsersReset(open.id), 'Ссылка на новый пароль отправлена');
                    }}
                  >
                    Ссылка на пароль
                  </Button>
                  <Button
                    variant="ghost"
                    tone="neutral"
                    iconLeft={<LogOut size={15} />}
                    onClick={() => {
                      act(siteUsersLogout(open.id), 'Вышел на всех устройствах');
                    }}
                  >
                    Выйти везде
                  </Button>
                  <Button
                    variant="ghost"
                    tone={open.blocked ? 'neutral' : 'danger'}
                    iconLeft={open.blocked ? <RotateCcw size={15} /> : <Ban size={15} />}
                    onClick={() => {
                      act(
                        siteUsersBlock(open.id, { blocked: !open.blocked }),
                        open.blocked ? 'Снова может входить' : 'Заблокирован',
                      );
                    }}
                  >
                    {open.blocked ? 'Разблокировать' : 'Заблокировать'}
                  </Button>
                </div>
              ) : undefined
            }
          >
            <div className={styles['badges']}>
              <State user={open} />
            </div>
            <dl className={styles['details']}>
              {shown.map((field) => (
                <div key={field.key} className={styles['details__row']}>
                  <dt>{field.name}</dt>
                  <dd>{valueText(open.data[field.key]) || '—'}</dd>
                </div>
              ))}
            </dl>
            <Link
              className={styles['link']}
              to="/sites/$siteId/$env/r/$recordId"
              params={{ siteId, env, recordId: open.id }}
            >
              <ExternalLink size={14} /> Править профиль
            </Link>
          </DialogContent>
        )}
      </Dialog>

      <NewVisitor
        siteId={siteId}
        env={env}
        open={creating}
        onOpenChange={setCreating}
        onCreated={refresh}
      />
    </div>
  );
}

function State({ user }: { user: SiteUserBody }) {
  if (user.blocked) {
    return (
      <Badge size="sm" tone="danger">
        заблокирован
      </Badge>
    );
  }

  const providers = (user.providers ?? []).map((p) => PROVIDER_NAME[p] ?? p).join(', ');

  return (
    <>
      {user.email_verified ? (
        <Badge size="sm" tone="success">
          почта подтверждена
        </Badge>
      ) : (
        <Badge size="sm" tone="warning">
          ждёт подтверждения
        </Badge>
      )}
      {providers !== '' && (
        <Badge size="sm" tone="neutral">
          {providers}
        </Badge>
      )}
    </>
  );
}

/**
 * Завести посетителя: почта сразу подтверждена. Без пароля ему уходит
 * письмо-приглашение — задать пароль самому.
 */
function NewVisitor({
  siteId,
  env,
  open,
  onOpenChange,
  onCreated,
}: {
  siteId: string;
  env: EnvKey;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);

    siteUsersCreate(siteId, kinds[env], {
      email: email.trim(),
      ...(name.trim() === '' ? {} : { data: { name: name.trim() } }),
      ...(password === '' ? {} : { password }),
    })
      .then(() => {
        toast.success(password === '' ? 'Приглашение отправлено' : 'Посетитель заведён');
        setEmail('');
        setName('');
        setPassword('');
        onOpenChange(false);
        onCreated();
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.detail : 'Не удалось завести');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <DialogContent
          title="Новый посетитель"
          description="Почта сразу считается подтверждённой. Без пароля посетитель получит письмо и задаст пароль сам."
          size="sm"
        >
          <form className={styles['form']} onSubmit={submit} noValidate>
            <Field label="Почта" required error={error}>
              <Input
                type="email"
                autoFocus
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
              />
            </Field>
            <Field label="Имя">
              <Input
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                }}
              />
            </Field>
            <Field
              label="Пароль"
              hint="Пусто — уйдёт письмо-приглашение. Иначе — не короче 8 знаков."
            >
              <Input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
            </Field>
            <Button type="submit" loading={busy} disabled={email.trim() === ''}>
              {password === '' ? 'Отправить приглашение' : 'Завести'}
            </Button>
          </form>
        </DialogContent>
      )}
    </Dialog>
  );
}
